

/* brand-hub.js — nundies locale/menu hydrate 2026-08-05 */

/* ─── §1  HANDOFF IIFE ──────────────────────────────────────────────────────
 * Runs immediately on parse (not deferred) so the next paint uses brand colours
 * before any async scripts execute. Paired with persistNavigationHandoffForNextPage().
 * Header logo is chosen server-side in blocks/_header-logo.liquid — no client swap on load.
 * ─────────────────────────────────────────────────────────────────────────── */
(function brandHubHandoff() {
  var STORAGE_KEY = 'inviziBrandHub';

  function localeRoot() {
    return (
      (typeof window !== 'undefined' &&
        window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root) ||
      '/'
    );
  }

  function stripLocale(p) {
    if (!p) return '/';
    var root = localeRoot();
    if (root === '/' || root === '') return p;
    var rootNoSlash = root.charAt(root.length - 1) === '/' ? root.slice(0, -1) : root;
    if (p === rootNoSlash || p === root) return '/';
    // Require a path boundary so "/fr" does not strip "/france..."
    var prefix = rootNoSlash + '/';
    if (p.indexOf(prefix) === 0) {
      return p.slice(rootNoSlash.length);
    }
    return p;
  }

  function normPath(p) {
    if (!p || p === '') return '/';
    var x = stripLocale(String(p));
    if (x.length > 1 && x.slice(-1) === '/') {
      return x.slice(0, -1);
    }
    return x.toLowerCase();
  }

  function applyFromStorage() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      var d = JSON.parse(raw);
      if (!d || d.v !== 1 || !d.targetPathname) {
        sessionStorage.removeItem(STORAGE_KEY);
        return;
      }

      // Only apply if we actually landed on the page the chip was navigating to.
      var here = normPath(window.location.pathname);
      var target = normPath(d.targetPathname);
      if (here !== target) {
        sessionStorage.removeItem(STORAGE_KEY);
        return;
      }

      var body = document.body;
      if (!body) return;

      body.classList.add('brand-hub-active');
      body.classList.remove('brand-hub--primary', 'brand-hub--secondary');
      body.classList.add(d.isSecondary ? 'brand-hub--secondary' : 'brand-hub--primary');
      if (d.themeKey) {
        body.dataset.brandHubThemeKey = d.themeKey;
      }
    } catch (e) {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
    }
  }

  applyFromStorage();

  // Expose so brand-pages logic can re-run it on bfcache restore (pageshow persisted).
  window.inviziBrandHubHandoffApply = applyFromStorage;
})();

/* ─── §2  SHARED UTILITIES ──────────────────────────────────────────────────
 * Single definitions used by both the brand-pages and logo-nav sections below.
 * Previously duplicated across brand-tabs-brand-pages.js and brand-tabs-logo-nav.js.
 * ─────────────────────────────────────────────────────────────────────────── */

/**
 * Get the locale root prefix (e.g. "/fr/" or "/" for the default locale).
 * Falls back to "/" if Shopify hasn't injected the routes object yet.
 * @returns {string}
 */
function getLocaleRoot() {
  const root =
    (typeof window !== 'undefined' &&
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root) ||
    '/';
  return root;
}

/**
 * Strip the locale prefix from a pathname so locale-prefixed URLs
 * compare equal to their canonical (default-locale) form.
 *   "/fr/products/foo" -> "/products/foo" when locale root is "/fr/"
 *   "/fr"              -> "/"             when locale root is "/fr/"
 *   "/products/foo"    -> "/products/foo"
 * @param {string} pathname
 * @returns {string}
 */
function stripLocalePrefix(pathname) {
  if (!pathname) return '/';
  const root = getLocaleRoot();
  if (root === '/' || root === '') return pathname;
  // root looks like "/fr/" — also accept the trailingless form "/fr"
  const rootNoSlash = root.endsWith('/') ? root.slice(0, -1) : root;
  if (pathname === rootNoSlash || pathname === root) return '/';
  // Require a path boundary so "/fr" does not strip "/france..."
  const prefix = `${rootNoSlash}/`;
  if (pathname.startsWith(prefix)) {
    return pathname.slice(rootNoSlash.length);
  }
  return pathname;
}

/**
 * Normalise a pathname: strip locale prefix, then strip trailing slash
 * (unless the path is just "/"). Lowercased for case-insensitive matching.
 * @param {string} pathname
 * @returns {string}
 */
function normalizePath(pathname) {
  if (!pathname || pathname === '') return '/';
  let p = stripLocalePrefix(pathname);
  if (p.length > 1 && p.endsWith('/')) {
    p = p.slice(0, -1);
  }
  return p.toLowerCase();
}

/**
 * @param {string} currentPath
 * @param {string} candidate
 * @returns {boolean}
 */
function pathMatches(currentPath, candidate) {
  const current = normalizePath(currentPath);
  const target = normalizePath(candidate);
  if (current === target) return true;

  // FR market resolves the Nundies brand page to a translated handle while the
  // public URL stays /pages/nundies — treat both landing aliases as equivalent.
  const isNundiesLanding = (p) => p === '/pages/nundies' || p.startsWith('/pages/nundies-');
  return isNundiesLanding(current) && isNundiesLanding(target);
}

/** @returns {boolean} */
function isDesignMode() {
  return typeof window.Shopify !== 'undefined' && window.Shopify.designMode === true;
}

/** @returns {boolean} */
function prefersReducedMotion() {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * @param {string} className
 * @returns {boolean}
 */
function hasBodyClass(className) {
  return document.body?.classList.contains(className) === true;
}

/** @returns {boolean} */
function disableBrandTabMenuAnimation() {
  return hasBodyClass('disable-brand-tab-menu-animation');
}

/** @returns {boolean} */
function disableBrandTabLogoAnimation() {
  return hasBodyClass('disable-brand-tab-logo-animation');
}

/** @returns {boolean} */
function disableBrandTabFontAnimation() {
  return hasBodyClass('disable-brand-tab-font-animation');
}

/**
 * Returns true when two asset URLs resolve to the same pathname, regardless of
 * origin, query params, or tracking tokens appended by Shopify.
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
function sameAssetUrl(a, b) {
  try {
    const ua = new URL(a, window.location.origin);
    const ub = new URL(b, window.location.origin);
    return ua.pathname === ub.pathname;
  } catch {
    return a === b;
  }
}

/**
 * Returns true when the anchor's href is same-origin (safe to intercept).
 * @param {HTMLAnchorElement} anchor
 * @returns {boolean}
 */
function sameOriginHref(anchor) {
  try {
    const u = new URL(anchor.href, window.location.origin);
    return u.origin === window.location.origin;
  } catch {
    return false;
  }
}

/* ─── §3  CONSTANTS ─────────────────────────────────────────────────────────── */

const HEADING_SELECTOR = 'h1, h2, h3, h4, .h1, .h2, .h3, .h4';
// h5/h6 are intentionally excluded — they are minor headings that don't need the
// font-swap animation treatment during brand transitions.

const HEADING_EXIT  = 'brand-hub-heading--exit-down';
const HEADING_ENTER = 'brand-hub-heading--enter';
const HEADING_MS    = { exit: 280, enter: 420 };

const BODY_BRAND_ACTIVE    = 'brand-hub-active';
const BODY_BRAND_PRIMARY   = 'brand-hub--primary';
const BODY_BRAND_SECONDARY = 'brand-hub--secondary';

const HANDOFF_STORAGE_KEY = 'inviziBrandHub';
const BRAND_KEY_ATTR      = 'data-brand-key';
const SECONDARY_BRAND_KEY = 'nundies';

const LOGO_HEADER_IMG_SELECTOR =
  '#brand-hub-header-logo-primary img.header-logo__image, [data-brand-hub-primary-logo] img.header-logo__image';
const LOGO_CLASS_BOP   = 'brand-hub-logo-swap--bop-up';
const LOGO_CLASS_EXIT  = 'brand-hub-logo-swap--exit-down';
const LOGO_CLASS_ENTER = 'brand-hub-logo-swap--enter';
const LOGO_MS = { bop: 200, exit: 280, enter: 420 };

const VIEW_CART_BUTTON_SELECTOR = '.header-actions__cart-icon--has-cart';

// Chip animation durations — must match --brand-chip-step-bg / --brand-chip-step-dot
// CSS custom properties declared in blocks/_brand-brands-hub.liquid.
const STEP_BG_MS        = 400;
const STEP_DOT_MS       = 450;
/** Both steps run in parallel; wait for the longer one + a small render buffer. */
const CHIP_ANIM_MS      = Math.max(STEP_BG_MS, STEP_DOT_MS) + 60;
const SELECT_STAGGER_MS = 28;

const MENU_EXIT_CLASS              = 'brand-hub-menu--exit-down';
const MENU_NAV_ENTER_CLASS         = 'brand-hub-menu--enter';
const MENU_ENTER_CLASS             = 'brand-hub-menu-incoming--enter';
const MENU_BOP_DELAY_MS            = LOGO_MS.bop;  // start drop when logo starts dropping
const MENU_EXIT_MS                 = LOGO_MS.exit; // matches brand-hub-logo-fall duration
const MENU_ENTER_MS                = LOGO_MS.enter;// matches brand-hub-logo-rise-in duration
const MENU_DEFAULT_TITLES_ATTR     = 'data-brand-default-titles';
const MENU_PRIMARY_TITLES_ATTR     = 'data-brand-primary-titles';
const MENU_SECONDARY_TITLES_ATTR   = 'data-brand-secondary-titles';
const MENU_HAS_PRIMARY_OVERRIDE_ATTR   = 'data-brand-has-primary-override';
const MENU_HAS_SECONDARY_OVERRIDE_ATTR = 'data-brand-has-secondary-override';

/** Blocks URL-based sync from reverting state set during a pre-navigation animation. */
let brandHubNavigationPending = false;

/* ─── §4  CART BUTTON THEME ─────────────────────────────────────────────── */

function clearViewCartButtonTheme() {
  const buttons = document.querySelectorAll(VIEW_CART_BUTTON_SELECTOR);
  for (const button of buttons) {
    button.style.removeProperty('--view-cart-pill-bg');
    button.style.removeProperty('--view-cart-pill-text');
  }
}

/** @param {boolean} isSecondary */
function applyViewCartButtonTheme(isSecondary) {
  const buttons = document.querySelectorAll(VIEW_CART_BUTTON_SELECTOR);
  if (!buttons.length) return;

  const styles = window.getComputedStyle(document.documentElement);
  const bg = styles
    .getPropertyValue(isSecondary ? '--brand-hub-secondary-bg' : '--brand-hub-primary-bg')
    .trim();
  const text = styles
    .getPropertyValue(isSecondary ? '--brand-hub-secondary-text' : '--brand-hub-primary-text')
    .trim();

  for (const button of buttons) {
    if (bg)   button.style.setProperty('--view-cart-pill-bg', bg);
    else      button.style.removeProperty('--view-cart-pill-bg');
    if (text) button.style.setProperty('--view-cart-pill-text', text);
    else      button.style.removeProperty('--view-cart-pill-text');
  }
}

/* ─── §5  BRAND ROLE DETECTION ──────────────────────────────────────────── */

/**
 * Resolve brand role from explicit data-brand-key first, then class fallback.
 * @param {Element | null} link
 * @returns {boolean}
 */
function isSecondaryBrandLink(link) {
  if (!link) return false;
  const brandKey = String(link.getAttribute(BRAND_KEY_ATTR) || '').trim().toLowerCase();
  if (brandKey) return brandKey === SECONDARY_BRAND_KEY;
  return link.classList.contains('brand-brands-hub__logo-link--style-secondary');
}

/**
 * Derive a stable theme key from data-brand-key + brand role.
 * Must stay aligned with layout/theme.liquid (e.g. "nundies|s", "invizi|p")
 * so locale prefixes / translated handles never cause a false theme clear.
 * @param {HTMLAnchorElement | null} link
 * @returns {string}
 */
function getBrandHubThemeKey(link) {
  if (!link) return '';
  const brandKey = String(link.getAttribute(BRAND_KEY_ATTR) || '')
    .trim()
    .toLowerCase();
  if (!brandKey) return '';
  const sec = isSecondaryBrandLink(link);
  return `${brandKey}|${sec ? 's' : 'p'}`;
}

/* ─── §6  BODY THEME ────────────────────────────────────────────────────── */

function clearBrandHubBodyTheme() {
  document.body.classList.remove(BODY_BRAND_ACTIVE, BODY_BRAND_PRIMARY, BODY_BRAND_SECONDARY);
  delete document.body.dataset.brandHubThemeKey;
  clearViewCartButtonTheme();
  try { sessionStorage.removeItem(HANDOFF_STORAGE_KEY); } catch { /* ignore */ }
}

/** @param {HTMLAnchorElement} link */
function applyBrandHubBodyThemeFromLink(link) {
  if (!link) return;
  const isSecondary = isSecondaryBrandLink(link);
  document.body.classList.add(BODY_BRAND_ACTIVE);
  document.body.classList.remove(BODY_BRAND_PRIMARY, BODY_BRAND_SECONDARY);
  document.body.classList.add(isSecondary ? BODY_BRAND_SECONDARY : BODY_BRAND_PRIMARY);
  document.body.dataset.brandHubThemeKey = getBrandHubThemeKey(link);
  applyViewCartButtonTheme(isSecondary);
}

/** @param {HTMLAnchorElement | null} activeLink */
function applyBrandHubBodyTheme(activeLink) {
  const nextKey = getBrandHubThemeKey(activeLink);
  const currentKey = document.body.dataset.brandHubThemeKey || '';
  if (currentKey === nextKey) return;

  // Do not wipe SSR brand colours when client path sync found no chip match.
  // Locale / translated-handle mismatches can miss in JS while Liquid matched.
  // Only preserve stable brand-key theme keys from layout/theme.liquid
  // (e.g. "nundies|s"), not legacy pathname keys.
  if (!activeLink) {
    const ssrBrandKey = /^(invizi|nundies)\|[ps]$/.test(currentKey);
    if (document.body.classList.contains(BODY_BRAND_ACTIVE) && ssrBrandKey) return;
    clearBrandHubBodyTheme();
    return;
  }

  clearBrandHubBodyTheme();
  applyBrandHubBodyThemeFromLink(activeLink);
}

/* ─── §7  NAVIGATION HANDOFF ────────────────────────────────────────────── */

/**
 * Persist brand state to sessionStorage so the next page can apply brand colours
 * before deferred scripts run (consumed by the handoff IIFE at the top of this file).
 * @param {HTMLAnchorElement} anchor
 */
function persistNavigationHandoffForNextPage(anchor) {
  if (!anchor) return;
  let targetPathname;
  try {
    targetPathname = normalizePath(new URL(anchor.href, window.location.origin).pathname);
  } catch {
    return;
  }

  const payload = {
    v: 1,
    targetPathname,
    themeKey: getBrandHubThemeKey(anchor),
    isSecondary: isSecondaryBrandLink(anchor),
  };

  try {
    sessionStorage.setItem(HANDOFF_STORAGE_KEY, JSON.stringify(payload));
  } catch { /* storage unavailable */ }
}

/**
 * Prevents URL-based sync from reverting pre-navigation animated state.
 * @param {boolean} pending
 */
function setNavigationPending(pending) {
  brandHubNavigationPending = Boolean(pending);
}

/* ─── §8  HEADING ANIMATION ─────────────────────────────────────────────── */

/**
 * Collect h1–h4 headings currently visible in the viewport.
 * Excludes headings inside header/brand-tabs — those animate via their own paths.
 * @returns {HTMLElement[]}
 */
function getVisibleHeadings() {
  const headings = document.querySelectorAll(HEADING_SELECTOR);
  const visible = [];
  for (const el of headings) {
    if (el.closest('#header-group, header, header-component, [data-section-type="header"], .brand-tabs')) continue;
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0) {
      visible.push(el);
    }
  }
  return visible;
}

/**
 * @param {HTMLElement[]} els
 * @param {string} cls
 * @param {number} fallbackMs
 * @returns {Promise<void>}
 */
function animateHeadingStep(els, cls, fallbackMs) {
  return new Promise((resolve) => {
    if (!els.length) return resolve();
    let remaining = els.length;
    let resolved = false;
    const done = () => { if (resolved) return; if (--remaining <= 0) { resolved = true; resolve(); } };
    const forceResolve = () => { if (!resolved) { resolved = true; resolve(); } };
    for (const el of els) {
      el.addEventListener('animationend', done, { once: true });
      el.classList.add(cls);
    }
    setTimeout(forceResolve, fallbackMs + 80);
  });
}

/** @param {HTMLElement[]} els */
function clearHeadingAnimClasses(els) {
  for (const el of els) {
    el.classList.remove(HEADING_EXIT, HEADING_ENTER);
    el.style.willChange = '';
  }
}

/**
 * Logo-style drop/rise animation on visible headings around a state swap.
 * Extra elements (e.g. menu items) can animate in sync with headings.
 *
 * 1. Fall away  — headings + extraEls all fall together
 * 2. applyFn()  — state changes while elements are invisible
 * 3. Rise in    — headings + extraEls all rise together
 *
 * The manual opacity toggle between steps 2 and 3 is intentional: removing the exit
 * class at zero opacity and then removing the inline opacity in the same frame would
 * cause a flash. Setting opacity:0 as an inline style holds the element invisible
 * across the class swap, and removing it after a forced reflow (offsetWidth) ensures
 * the enter animation starts from the correct initial state.
 *
 * @param {() => void} applyFn
 * @param {HTMLElement[]} [extraEls]
 */
async function animateHeadingFontSwap(applyFn, extraEls = []) {
  const headings = getVisibleHeadings();
  const allEls = [...headings, ...extraEls];

  if (!allEls.length || prefersReducedMotion() || disableBrandTabFontAnimation()) {
    applyFn();
    return;
  }

  /* Step 1: drop down out of sight */
  await animateHeadingStep(allEls, HEADING_EXIT, HEADING_MS.exit);

  /* Step 2: apply the state change (elements are invisible at opacity 0 from animation fill) */
  applyFn();

  /* Step 3: hold at opacity 0 across the class swap, then trigger rise */
  for (const el of allEls) {
    el.classList.remove(HEADING_EXIT);
    el.style.opacity = '0';
  }
  void allEls[0]?.offsetWidth; // forced reflow so the enter class sees a clean start state
  for (const el of allEls) el.style.removeProperty('opacity');
  await animateHeadingStep(allEls, HEADING_ENTER, HEADING_MS.enter);

  clearHeadingAnimClasses(allEls);
}

/* ─── §9  HEADER LOGO SWAP ──────────────────────────────────────────────── */

/** @returns {HTMLImageElement | null} */
function getHeaderLogoImage() {
  const el = document.querySelector(LOGO_HEADER_IMG_SELECTOR);
  return el instanceof HTMLImageElement ? el : null;
}

/**
 * @param {string} whiteUrl
 * @returns {Promise<void>}
 */
function preloadImage(whiteUrl) {
  return new Promise((resolve) => {
    const im = new Image();
    im.onload = () => resolve();
    im.onerror = () => resolve();
    im.src = whiteUrl;
  });
}

/**
 * @param {HTMLImageElement} img
 * @param {number} msFallback
 * @returns {Promise<void>}
 */
function waitForLogoAnimationEnd(img, msFallback) {
  return new Promise((resolve) => {
    const done = () => resolve();
    img.addEventListener('animationend', done, { once: true });
    window.setTimeout(done, msFallback + 80);
  });
}

/** @param {HTMLImageElement} img */
function clearLogoSwapClasses(img) {
  img.classList.remove(LOGO_CLASS_BOP, LOGO_CLASS_EXIT, LOGO_CLASS_ENTER);
  img.style.willChange = '';
}

/**
 * @param {HTMLImageElement} img
 * @param {{ desktop?: string, mobile?: string }} [dims]
 */
function applyHeaderLogoDimensions(img, dims) {
  if (!img || !dims) return;
  const desktop = parseInt(dims.desktop || '', 10);
  const mobile  = parseInt(dims.mobile  || '', 10);
  if (Number.isFinite(desktop) && desktop > 0) img.style.setProperty('--header-logo-image-width', `${desktop}px`);
  if (Number.isFinite(mobile)  && mobile  > 0) img.style.setProperty('--header-logo-image-width-mobile', `${mobile}px`);
}

/**
 * @param {HTMLImageElement} img
 * @param {string} whiteUrl
 * @returns {boolean}
 */
function headerLogoShowsBrandAsset(img, whiteUrl) {
  if (!whiteUrl) return false;
  const cur = img.currentSrc || img.src;
  return sameAssetUrl(cur, whiteUrl);
}

/**
 * Animated header logo transition — used only during chip navigation pre-flight.
 * The server-rendered Liquid logo remains the source of truth on actual page load.
 * @param {string} whiteUrl
 * @param {{ desktop?: string, mobile?: string }} [dims]
 * @returns {Promise<void>}
 */
async function transitionHeaderLogoToBrandUrl(whiteUrl, dims) {
  const img = getHeaderLogoImage();
  if (!img || !whiteUrl) return;

  if (headerLogoShowsBrandAsset(img, whiteUrl)) {
    applyHeaderLogoDimensions(img, dims);
    return;
  }

  if (prefersReducedMotion() || disableBrandTabLogoAnimation()) {
    applyHeaderLogoDimensions(img, dims);
    img.src = whiteUrl;
    img.removeAttribute('srcset');
    return;
  }

  await preloadImage(whiteUrl);

  clearLogoSwapClasses(img);
  void img.offsetWidth;
  img.classList.add(LOGO_CLASS_BOP);
  await waitForLogoAnimationEnd(img, LOGO_MS.bop);

  img.classList.remove(LOGO_CLASS_BOP);
  img.classList.add(LOGO_CLASS_EXIT);
  await waitForLogoAnimationEnd(img, LOGO_MS.exit);

  // Switch dimensions alongside the incoming logo (not the outgoing one).
  applyHeaderLogoDimensions(img, dims);
  img.src = whiteUrl;
  img.removeAttribute('srcset');
  try { if (img.decode) await img.decode(); } catch { /* optional decode */ }

  img.style.opacity = '0';
  img.classList.remove(LOGO_CLASS_EXIT);
  void img.offsetWidth;
  img.classList.add(LOGO_CLASS_ENTER);
  window.requestAnimationFrame(() => img.style.removeProperty('opacity'));
  await waitForLogoAnimationEnd(img, LOGO_MS.enter);
  img.classList.remove(LOGO_CLASS_ENTER);
}

/* ─── §10  BRAND MENU TRANSITION ────────────────────────────────────────── */

/**
 * @param {Element | null} headerMenu
 * @param {boolean} incomingIsSecondary
 * @returns {string[]}
 */
function getIncomingMenuTitles(headerMenu, incomingIsSecondary) {
  if (!headerMenu) return [];

  const hasOverride = incomingIsSecondary
    ? headerMenu.hasAttribute(MENU_HAS_SECONDARY_OVERRIDE_ATTR)
    : headerMenu.hasAttribute(MENU_HAS_PRIMARY_OVERRIDE_ATTR);

  const overrideTitlesAttr = incomingIsSecondary ? MENU_SECONDARY_TITLES_ATTR : MENU_PRIMARY_TITLES_ATTR;

  /** @param {string} attr */
  const parseTitles = (attr) => {
    try {
      const parsed = JSON.parse(headerMenu.getAttribute(attr) || '[]');
      return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string' && v.trim() !== '') : [];
    } catch {
      return [];
    }
  };

  if (hasOverride) {
    const overrideTitles = parseTitles(overrideTitlesAttr);
    if (overrideTitles.length) return overrideTitles;
  }

  return parseTitles(MENU_DEFAULT_TITLES_ATTR);
}

/**
 * Animate the desktop header menu when switching brands.
 *
 * Timing is lockstep with the logo swap animation:
 *   Step 1 — wait for logo bop phase (200ms)
 *   Step 2 — current nav items fall away (280ms), in sync with logo exit
 *   Step 3 — incoming brand link titles rise in (420ms), in sync with logo enter
 *
 * JS creates a visual-only overlay; the real menu renders correctly on the new page.
 *
 * @param {boolean} incomingIsSecondary
 * @returns {Promise<void>}
 */
async function animateBrandMenuTransition(incomingIsSecondary) {
  if (prefersReducedMotion() || disableBrandTabMenuAnimation()) return;

  const headerMenu = document.querySelector('header-menu[ref="headerMenu"]');
  if (!headerMenu) return;

  const titles     = getIncomingMenuTitles(headerMenu, incomingIsSecondary);
  const currentNav = headerMenu.querySelector('nav[header-menu]');
  if (!currentNav) return;

  // .menu-list carries the --menu-top-level-* CSS vars via inline style.
  // The incoming overlay must sit inside it to inherit those vars and match spacing.
  const menuListEl = currentNav.querySelector('.menu-list');
  if (!menuListEl) return;

  /** @type {HTMLElement | null} */
  let incoming  = null;
  let completed = false;

  const removeIncomingOverlays = () => {
    for (const el of menuListEl.querySelectorAll('.brand-hub-menu-incoming')) el.remove();
  };

  // Ensure no stale state from an interrupted previous transition.
  currentNav.classList.remove(MENU_EXIT_CLASS, MENU_NAV_ENTER_CLASS);
  headerMenu.classList.remove('brand-hub-menu-show-incoming', 'brand-hub-menu-animating');
  removeIncomingOverlays();

  try {
    headerMenu.classList.add('brand-hub-menu-animating');

    // Step 1: mirror the logo bop phase before drop starts.
    await new Promise((resolve) => setTimeout(resolve, MENU_BOP_DELAY_MS));

    // Step 2: fall the current nav away (lockstep with logo exit).
    void currentNav.offsetWidth;
    currentNav.classList.add(MENU_EXIT_CLASS);
    await new Promise((resolve) => {
      let done = false;
      const finish = () => { if (!done) { done = true; resolve(); } };
      currentNav.addEventListener('animationend', finish, { once: true });
      setTimeout(finish, MENU_EXIT_MS + 80);
    });

    // Step 3: rise the current nav layer back while incoming parent titles rise in.
    currentNav.classList.remove(MENU_EXIT_CLASS);
    void currentNav.offsetWidth;
    currentNav.classList.add(MENU_NAV_ENTER_CLASS);
    headerMenu.classList.add('brand-hub-menu-show-incoming');

    const risePromises = [
      new Promise((resolve) => {
        let done = false;
        const finish = () => { if (!done) { done = true; resolve(); } };
        currentNav.addEventListener('animationend', finish, { once: true });
        setTimeout(finish, MENU_ENTER_MS + 80);
      }),
    ];

    if (titles.length) {
      incoming = document.createElement('ul');
      incoming.className = 'brand-hub-menu-incoming';
      // Inherit the same inline CSS vars that drive font family, size, weight, spacing.
      incoming.setAttribute('style', menuListEl.getAttribute('style') || '');
      for (const title of titles) {
        const li        = document.createElement('li');
        li.className    = 'menu-list__list-item';
        const link      = document.createElement('span');
        link.className  = 'menu-list__link';
        const linkTitle = document.createElement('span');
        linkTitle.className   = 'menu-list__link-title';
        linkTitle.textContent = title;
        link.append(linkTitle);
        li.append(link);
        incoming.append(li);
      }
      menuListEl.append(incoming);
      void incoming.offsetWidth;
      incoming.classList.add(MENU_ENTER_CLASS);

      risePromises.push(new Promise((resolve) => {
        let done = false;
        const finish = () => { if (!done) { done = true; resolve(); } };
        incoming?.addEventListener('animationend', finish, { once: true });
        setTimeout(finish, MENU_ENTER_MS + 80);
      }));
    }

    await Promise.all(risePromises);
    completed = true;
  } finally {
    // On success: leave final incoming state visible until navigation fires.
    // On interruption: fully clean up so the next click starts fresh.
    if (!completed) {
      currentNav.classList.remove(MENU_EXIT_CLASS, MENU_NAV_ENTER_CLASS);
      headerMenu.classList.remove('brand-hub-menu-show-incoming', 'brand-hub-menu-animating');
      removeIncomingOverlays();
    }
  }
}

/* ─── §11  BRAND STATE SYNC ─────────────────────────────────────────────── */

/**
 * Previously rebuilt desktop + mobile nav from hard-coded / chip collection
 * paths when Nundies was active. That overrode the designed nundies-menu and
 * caused a flat unstyled list outside the theme editor. Kept as a no-op so
 * call sites stay stable; Liquid uses settings.nundies_menu_override only.
 */
function hydrateNundiesMenuFromBrandPaths() {
  return;
}

/**
 * Read the current URL, mark the matching chip as active, and apply body theme.
 * Skipped when in Shopify design-mode or while a navigation animation is in flight.
 */
function syncAndApplyBrandHubState() {
  if (isDesignMode()) return;
  if (brandHubNavigationPending) return;

  const links = document.querySelectorAll('.brand-brands-hub__logo-link[data-brand-page-paths]');
  if (!links.length) {
    // Keep SSR theme if Liquid already painted brand-hub classes.
    if (!(document.body.classList.contains(BODY_BRAND_ACTIVE) && document.body.dataset.brandHubThemeKey)) {
      clearBrandHubBodyTheme();
    }
    return;
  }

  const currentPath = normalizePath(window.location.pathname);
  /** @type {HTMLAnchorElement | null} */
  let firstActive = null;
  /** @type {HTMLAnchorElement | null} */
  let ssrActive = null;

  for (const el of links) {
    const link = /** @type {HTMLAnchorElement} */ (el);
    if (
      !ssrActive &&
      (link.classList.contains('brand-brands-hub__logo-link--active') ||
        link.getAttribute('aria-current') === 'page')
    ) {
      ssrActive = link;
    }

    let paths = [];
    try {
      paths = JSON.parse(link.getAttribute('data-brand-page-paths') || '[]');
    } catch {
      paths = [];
    }

    const isActive = Array.isArray(paths) && paths.some((p) => pathMatches(currentPath, p));
    link.classList.toggle('brand-brands-hub__logo-link--active', isActive);
    if (isActive) {
      link.setAttribute('aria-current', 'page');
      if (!firstActive) firstActive = link;
    } else {
      link.removeAttribute('aria-current');
    }
  }

  // Prefer path match; fall back to Liquid SSR active chip so locale misses
  // do not deselect the tab or clear colours/menu branding.
  const activeLink = firstActive || ssrActive;
  if (!firstActive && ssrActive) {
    ssrActive.classList.add('brand-brands-hub__logo-link--active');
    ssrActive.setAttribute('aria-current', 'page');
  }

  applyBrandHubBodyTheme(activeLink);
  hydrateNundiesMenuFromBrandPaths();
}

/* ─── §12  CHIP NAVIGATION (pre-flight animation) ───────────────────────── */

/**
 * Enable CSS transitions on brand logo chips after the first paint so they don't
 * animate in on page load. Uses double rAF to ensure the initial paint is committed.
 * @param {NodeListOf<HTMLAnchorElement> | HTMLAnchorElement[]} links
 */
function enableChipTransitions(links) {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      for (const el of links) {
        el.classList.add('brand-brands-hub__logo-link--transitions-ready');
      }
    });
  });
}

/** @param {HTMLAnchorElement} link */
async function animateChipDeselect(link) {
  link.classList.add('brand-brands-hub__logo-link--anim-deselect');
  await new Promise((r) => window.setTimeout(r, CHIP_ANIM_MS));
  link.classList.remove(
    'brand-brands-hub__logo-link--active',
    'brand-brands-hub__logo-link--anim-deselect'
  );
  link.removeAttribute('aria-current');
}

/** @param {HTMLAnchorElement} link */
async function animateChipSelect(link) {
  link.classList.add('brand-brands-hub__logo-link--active');
  link.setAttribute('aria-current', 'page');
  await new Promise((r) => window.setTimeout(r, CHIP_ANIM_MS));
}

/** @param {string} href */
function navigateTo(href) {
  window.location.assign(href);
}

/**
 * Attach the pre-navigation chip click handler to the brand-tabs container.
 * Intercepts same-origin, non-modifier clicks on inactive chips and runs the
 * full animation sequence before navigating.
 */
function initLogoNav() {
  if (isDesignMode()) return;

  const tabs = document.querySelector('.brand-tabs');
  if (!tabs) return;

  const links = /** @type {NodeListOf<HTMLAnchorElement>} */ (
    tabs.querySelectorAll('.brand-brands-hub__logo-link[href]')
  );
  if (!links.length) return;

  enableChipTransitions(links);

  tabs.addEventListener('click', (event) => {
    const anchor = /** @type {HTMLElement | null} */ (event.target)
      ?.closest?.('a.brand-brands-hub__logo-link');

    if (!anchor || !tabs.contains(anchor)) return;

    // Let browser handle modifier clicks (new tab, etc.) and already-prevented events.
    if (
      event.defaultPrevented ||
      event.button  !== 0    ||
      event.metaKey  ||
      event.ctrlKey  ||
      event.shiftKey ||
      event.altKey
    ) return;

    const href = anchor.getAttribute('href');
    if (!href || href === '#' || href.startsWith('#')) return;
    if (!sameOriginHref(anchor)) return;
    if (prefersReducedMotion()) return;

    // Already on this brand — let the browser navigate normally without animation.
    if (anchor.classList.contains('brand-brands-hub__logo-link--active')) return;

    event.preventDefault();

    const fullHref     = new URL(anchor.href, window.location.origin).href;
    const currentActive = tabs.querySelector('.brand-brands-hub__logo-link--active');

    const brandKey    = String(anchor.getAttribute('data-brand-key') || '').trim().toLowerCase();
    const isSecondary = brandKey
      ? brandKey === SECONDARY_BRAND_KEY
      : anchor.classList.contains('brand-brands-hub__logo-link--style-secondary');

    const whiteUrl      = anchor.getAttribute('data-brand-logo-white-url') || '';
    const headerLogoDims = {
      desktop: anchor.getAttribute('data-brand-header-logo-width-desktop') || '',
      mobile:  anchor.getAttribute('data-brand-header-logo-width-mobile')  || '',
    };

    const run = async () => {
      try {
        setNavigationPending(true);
        // Failsafe: unlock if navigation is somehow interrupted (e.g. browser back).
        // The flag is also cleared immediately after navigateTo() is called below.
        const failsafeTimer = window.setTimeout(() => setNavigationPending(false), 3000);

        if (currentActive && currentActive !== anchor) {
          await animateChipDeselect(/** @type {HTMLAnchorElement} */ (currentActive));
        }

        if (!anchor.classList.contains('brand-brands-hub__logo-link--active')) {
          await new Promise((r) => window.setTimeout(r, SELECT_STAGGER_MS));

          /* All animations run in parallel:
             - heading font swap (exit → apply colour → rise)
             - chip select animation
             - header logo swap
             - desktop menu transition (fall + rise) */
          await Promise.all([
            animateHeadingFontSwap(() => applyBrandHubBodyThemeFromLink(anchor)),
            animateChipSelect(anchor),
            whiteUrl ? transitionHeaderLogoToBrandUrl(whiteUrl, headerLogoDims) : Promise.resolve(),
            animateBrandMenuTransition(isSecondary),
          ]);
        }

        persistNavigationHandoffForNextPage(anchor);

        // Navigation is imminent — clear the pending flag before we leave.
        window.clearTimeout(failsafeTimer);
        setNavigationPending(false);
        navigateTo(fullHref);
      } catch {
        // Animation failed — navigate unconditionally so the user isn't stuck.
        setNavigationPending(false);
        navigateTo(fullHref);
      }
    };

    void run();
  }, false);
}

/* ─── §13  PUBLIC API ───────────────────────────────────────────────────── */

if (typeof window !== 'undefined') {
  window.inviziBrandHub = {
    clearTheme:                      clearBrandHubBodyTheme,
    applyThemeFromLink:              applyBrandHubBodyThemeFromLink,
    transitionHeaderLogoToBrandUrl,
    animateHeadingFontSwap,
    syncAndApplyBrandHubState,
    persistNavigationHandoffForNextPage,
    setNavigationPending,
    animateBrandMenuTransition,
  };
}

/* ─── §14  INIT ─────────────────────────────────────────────────────────── */

function init() {
  if (isDesignMode()) return;

  // Re-run the handoff in case brand-hub.js loads after the IIFE already fired
  // (e.g. on a bfcache restore where the IIFE reference is stale).
  if (typeof window.inviziBrandHubHandoffApply === 'function') {
    window.inviziBrandHubHandoffApply();
  }

  syncAndApplyBrandHubState();
  initLogoNav();

  // Re-sync on full load (handles images/fonts that affect layout after DOMContentLoaded).
  window.addEventListener('load', () => syncAndApplyBrandHubState(), { once: true });

  // Re-sync when Shopify injects a section in the theme editor.
  document.addEventListener('shopify:section:load', () => syncAndApplyBrandHubState());

  // bfcache restore: clear navigation lock, reapply handoff, re-sync.
  window.addEventListener('pageshow', (event) => {
    setNavigationPending(false);
    if (event.persisted && typeof window.inviziBrandHubHandoffApply === 'function') {
      window.inviziBrandHubHandoffApply();
    }
    if (event.persisted) {
      syncAndApplyBrandHubState();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
